import { useState, useEffect } from 'react';
import { useOutletContext } from 'react-router-dom';
import axios from 'axios';
import { 
  FileText, 
  Save,
  RefreshCw,
  Globe,
  Mail,
  Phone,
  Building2
} from 'lucide-react';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Textarea } from '@/components/ui/textarea';
import { toast } from 'sonner';

import { API_BASE_URL } from '../utils/api';
const API_URL = API_BASE_URL;

export default function AdminContent() {
  const { admin } = useOutletContext();
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [settings, setSettings] = useState({
    company_name: '',
    website: '',
    contact_email: '',
    contact_phone: '',
    description: '',
    address: ''
  });

  useEffect(() => {
    fetchSettings();
  }, []);

  const fetchSettings = async () => {
    try {
      const token = localStorage.getItem('admin_token');
      const response = await axios.get(`${API_URL}/api/admin-portal/content-settings`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      if (response.data) {
        setSettings(prev => ({ ...prev, ...response.data }));
      }
    } catch (error) {
      toast.error('Failed to load content settings');
    } finally {
      setLoading(false);
    }
  };

  const handleSave = async () => {
    setSaving(true);
    try {
      const token = localStorage.getItem('admin_token');
      await axios.put(`${API_URL}/api/admin-portal/content-settings`, settings, {
        headers: { Authorization: `Bearer ${token}` }
      });
      toast.success('Content settings saved');
    } catch (error) {
      toast.error('Failed to save content settings');
    } finally {
      setSaving(false);
    }
  };

  if (loading) {
    return (
      <div className="p-8 flex items-center justify-center min-h-[400px]">
        <div className="animate-spin w-8 h-8 border-2 border-red-500 border-t-transparent rounded-full" />
      </div>
    );
  }

  return (
    <div className="p-8">
      {/* Header */}
      <div className="flex items-center justify-between mb-8">
        <div>
          <h1 className="text-3xl font-bold text-white">Content</h1>
          <p className="text-slate-400 mt-1">
            Manage company information displayed on public pages
          </p>
        </div>
        <Button onClick={handleSave} disabled={saving} className="bg-red-500 hover:bg-red-600">
          {saving ? <RefreshCw className="w-4 h-4 mr-2 animate-spin" /> : <Save className="w-4 h-4 mr-2" />}
          Save Changes
        </Button>
      </div>

      {/* Company Information */}
      <Card className="bg-slate-800/50 border-slate-700 mb-6">
        <CardHeader>
          <CardTitle className="text-white flex items-center gap-2">
            <Building2 className="w-5 h-5 text-blue-400" />
            Company Information
          </CardTitle>
          <CardDescription className="text-slate-400">
            This information appears on the About page and throughout the application
          </CardDescription>
        </CardHeader>
        <CardContent className="space-y-4">
          <div className="grid gap-4 sm:grid-cols-2">
            <div className="space-y-2">
              <Label className="text-slate-300">Company Name</Label>
              <div className="relative">
                <Building2 className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-slate-500" />
                <Input
                  className="pl-10 bg-slate-900/50 border-slate-600 text-white"
                  placeholder="Your Company Name"
                  value={settings.company_name}
                  onChange={e => setSettings({ ...settings, company_name: e.target.value })}
                />
              </div>
            </div>
            <div className="space-y-2">
              <Label className="text-slate-300">Website</Label>
              <div className="relative">
                <Globe className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-slate-500" />
                <Input
                  className="pl-10 bg-slate-900/50 border-slate-600 text-white"
                  placeholder="https://yourcompany.com"
                  value={settings.website}
                  onChange={e => setSettings({ ...settings, website: e.target.value })}
                />
              </div>
            </div>
            <div className="space-y-2">
              <Label className="text-slate-300">Contact Email</Label>
              <div className="relative">
                <Mail className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-slate-500" />
                <Input
                  type="email"
                  className="pl-10 bg-slate-900/50 border-slate-600 text-white"
                  placeholder="support@yourcompany.com"
                  value={settings.contact_email}
                  onChange={e => setSettings({ ...settings, contact_email: e.target.value })}
                />
              </div>
            </div>
            <div className="space-y-2">
              <Label className="text-slate-300">Contact Phone</Label>
              <div className="relative">
                <Phone className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-slate-500" />
                <Input
                  className="pl-10 bg-slate-900/50 border-slate-600 text-white"
                  placeholder="+64 9 123 4567"
                  value={settings.contact_phone}
                  onChange={e => setSettings({ ...settings, contact_phone: e.target.value })}
                />
              </div>
            </div>
          </div>
          
          <div className="space-y-2">
            <Label className="text-slate-300">Company Description</Label>
            <Textarea
              className="bg-slate-900/50 border-slate-600 text-white min-h-[100px]"
              placeholder="Brief description of your company..."
              value={settings.description || ''}
              onChange={e => setSettings({ ...settings, description: e.target.value })}
            />
          </div>
          
          <div className="space-y-2">
            <Label className="text-slate-300">Address</Label>
            <Input
              className="bg-slate-900/50 border-slate-600 text-white"
              placeholder="123 Main Street, Auckland, New Zealand"
              value={settings.address || ''}
              onChange={e => setSettings({ ...settings, address: e.target.value })}
            />
          </div>
        </CardContent>
      </Card>

      {/* Info Card */}
      <Card className="bg-slate-800/50 border-slate-700">
        <CardContent className="py-6">
          <div className="flex items-start gap-4">
            <div className="w-10 h-10 rounded-lg bg-blue-500/10 flex items-center justify-center flex-shrink-0">
              <FileText className="w-5 h-5 text-blue-400" />
            </div>
            <div>
              <h3 className="font-medium text-white mb-1">Page Content</h3>
              <p className="text-sm text-slate-400">
                The About, Help, Privacy Policy, and Terms & Conditions pages use default content. 
                The company information above is displayed on the About page. 
                For custom page content, contact your development team.
              </p>
            </div>
          </div>
        </CardContent>
      </Card>
    </div>
  );
}
